import { useEffect, useMemo, useRef, useState } from 'react';
import { BustEvent, DartInput, GameConfig, GameState, Player, TurnBannerData } from '../types/darts';
import {
  Match,
  addDart,
  addTurnTotal,
  canUndo,
  createMatch,
  deriveState,
  isX01,
  minDartsToFinish,
  startNextLeg,
  toHistoryRecord,
  undo
} from '../engine/match';
import { deleteMatchRecord, getActiveMatch, hasMatchRecord, saveActiveMatch, saveMatchRecord } from '../utils/storage';
import type { CheckoutPromptRequest } from '../components/CheckoutPrompt';
import type { Language } from '../utils/i18n';
import { audioEngine } from '../utils/audio';

const fireConfetti = () => {
  // Confetti wordt pas geladen als er iets te vieren is.
  import('canvas-confetti')
    .then(({ default: confetti }) => confetti({ particleCount: 120, spread: 90, origin: { y: 0.6 } }))
    .catch(() => {});
};

/**
 * De lopende wedstrijd: invoer, herstellen, legs, opslaan en de feedback (geluid, stem, bust, beurtmelding).
 * De stand zelf komt altijd uit de spelmotor (deriveState).
 */
export function useMatch({ language, onHistoryChange }: { language: Language; onHistoryChange: () => void }) {
  const [match, setMatch] = useState<Match | null>(getActiveMatch);
  const gameState: GameState | null = useMemo(() => (match ? deriveState(match) : null), [match]);

  const [bustEvent, setBustEvent] = useState<BustEvent | null>(null);
  const [turnBanner, setTurnBanner] = useState<TurnBannerData | null>(null);
  const [checkoutPrompt, setCheckoutPrompt] = useState<CheckoutPromptRequest | null>(null);
  const bustTimer = useRef<number | undefined>(undefined);
  const bannerTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    saveActiveMatch(match);
  }, [match]);

  // Een afgelopen wedstrijd staat in de geschiedenis; wordt de winnende worp hersteld, dan gaat hij er weer uit.
  useEffect(() => {
    if (!match || !gameState) return;
    if (gameState.isMatchOver) {
      saveMatchRecord(toHistoryRecord(match, gameState));
    } else if (hasMatchRecord(match.id)) {
      deleteMatchRecord(match.id);
    } else {
      return;
    }
    onHistoryChange();
  }, [match, gameState, onHistoryChange]);

  useEffect(
    () => () => {
      window.clearTimeout(bustTimer.current);
      window.clearTimeout(bannerTimer.current);
    },
    []
  );

  const clearFeedback = () => {
    window.clearTimeout(bustTimer.current);
    window.clearTimeout(bannerTimer.current);
    setBustEvent(null);
    setTurnBanner(null);
  };

  // Past nieuwe invoer toe en geeft geluid/beeld-feedback op basis van het verschil met de vorige stand.
  const apply = (next: Match) => {
    const prev = gameState;
    const nextState = deriveState(next);
    setMatch(next);
    // Nieuwe invoer: de melding van de vorige beurt heeft zijn werk gedaan.
    window.clearTimeout(bannerTimer.current);
    setTurnBanner(null);
    if (!prev || nextState.turnHistory.length <= prev.turnHistory.length) return;

    const turn = nextState.turnHistory[nextState.turnHistory.length - 1];
    const mode = nextState.config.gameMode;
    const legWon = nextState.isLegOver && !prev.isLegOver;

    if (mode !== 'cricket') {
      if (turn.turnTotal === 180) {
        audioEngine.play180();
        fireConfetti();
      }
      if (turn.isBust) {
        window.clearTimeout(bustTimer.current);
        setBustEvent({ playerId: turn.playerId, playerName: turn.playerName, score: turn.scoreBefore });
        bustTimer.current = window.setTimeout(() => setBustEvent(null), 2000);
        audioEngine.playBust();
      }
      audioEngine.speakTurnScore(
        turn.turnTotal,
        turn.isBust ? turn.scoreBefore : turn.scoreAfter,
        turn.isBust,
        language,
        legWon,
        turn.playerName,
        mode
      );

      // Bij een gewonnen leg toont het leg-venster de uitslag al.
      window.clearTimeout(bannerTimer.current);
      if (!legWon) {
        setTurnBanner({
          playerName: turn.playerName,
          scoreBefore: turn.scoreBefore,
          turnTotal: turn.attemptedTotal ?? turn.turnTotal,
          scoreAfter: turn.scoreAfter,
          isBust: turn.isBust
        });
        bannerTimer.current = window.setTimeout(() => setTurnBanner(null), 2500);
      }
    }

    if (legWon) {
      const winner = nextState.players.find(p => p.id === nextState.legWinnerId);
      audioEngine.playVictory();
      if (winner) audioEngine.speakWinner(winner.name, language);
    }
  };

  const start = (config: GameConfig, players: Player[]) => {
    clearFeedback();
    setCheckoutPrompt(null);
    setMatch(createMatch(config, players));
  };

  /** Stopt de wedstrijd; geeft de oude wedstrijd terug zodat hij hersteld kan worden. */
  const stop = (): Match | null => {
    const previous = match;
    clearFeedback();
    setCheckoutPrompt(null);
    setMatch(null);
    return previous;
  };

  const resume = (previous: Match) => setMatch(previous);

  // Wisselen kan alleen aan het begin van een beurt; Cricket gaat altijd per pijl.
  const canSwitchInput = !!gameState && gameState.config.gameMode !== 'cricket' && gameState.currentTurnDarts.length === 0;
  const inputMethod = gameState?.config.gameMode === 'cricket' ? 'per-dart' : gameState?.config.inputMethod;

  const switchInputMethod = () => {
    if (!match || !canSwitchInput) return;
    const nextMethod = match.config.inputMethod === 'per-dart' ? 'turn-total' : 'per-dart';
    setMatch({ ...match, config: { ...match.config, inputMethod: nextMethod } });
  };

  const throwDart = (dart: DartInput) => {
    if (!match || !gameState || gameState.isLegOver) return;
    audioEngine.playDartHit(dart.multiplier);

    // Speciale darts afroepen (triple 20, bull); een 180 krijgt zijn eigen fanfare.
    if (dart.value === 20 && dart.multiplier === 3) {
      const turnDarts = [...gameState.currentTurnDarts, dart];
      const turnSum = turnDarts.reduce((acc, d) => acc + d.score, 0);
      if (turnDarts.length !== 3 || turnSum !== 180) audioEngine.speakTriple20();
    } else if (dart.value === 25) {
      audioEngine.speakBull(dart.multiplier === 2);
    }

    apply(addDart(match, dart));
  };

  const submitTurnTotal = (turnTotal: number) => {
    if (!match || !gameState || gameState.isLegOver) return;
    audioEngine.playDartHit(turnTotal >= 100 ? 3 : 1);

    if (isX01(gameState.config) && gameState.config.outMode !== 'single') {
      const player = gameState.players[gameState.activePlayerIndex];
      const before = gameState.scores[player.id];
      const remaining = before - turnTotal;
      const minDarts = minDartsToFinish(before, gameState.config.outMode);
      if (remaining === 0 && minDarts !== null) {
        setCheckoutPrompt({ total: turnTotal, isFinish: true, minDarts });
        return;
      }
      // Binnen bereik van een dubbel: vraag hoeveel darts op de dubbel gingen (voor het uitgooi-percentage).
      if (minDarts !== null && minDarts <= 2 && remaining !== 0) {
        setCheckoutPrompt({ total: turnTotal, isFinish: false, minDarts });
        return;
      }
    }

    apply(addTurnTotal(match, turnTotal));
  };

  const confirmCheckout = (opts: { darts: number; dartsAtDouble: number }) => {
    if (!match || !checkoutPrompt) return;
    const total = checkoutPrompt.total;
    setCheckoutPrompt(null);
    apply(addTurnTotal(match, total, opts));
  };

  const cancelCheckout = () => setCheckoutPrompt(null);

  const undoLast = () => {
    if (!match || !canUndo(match)) return;
    clearFeedback();
    setCheckoutPrompt(null);
    setMatch(undo(match));
  };

  const nextLeg = () => {
    if (!match) return;
    clearFeedback();
    setMatch(startNextLeg(match));
  };

  return {
    match,
    gameState,
    bustEvent,
    turnBanner,
    checkoutPrompt,
    canUndo: !!match && canUndo(match),
    canSwitchInput,
    inputMethod,
    start,
    stop,
    resume,
    switchInputMethod,
    throwDart,
    submitTurnTotal,
    confirmCheckout,
    cancelCheckout,
    undoLast,
    nextLeg
  };
}

export type MatchController = ReturnType<typeof useMatch>;
